import type { ClipUploadFile } from "@components/ClipUpload/ClipUpload";
import {
  UserProductCategorySource,
  UserProductImageSource,
  UserProductSellingPointSource,
  UserProductUpsertRequestSourceType,
} from "@apis/generated/openapi";

export type CreateProductMode = "link" | "manual" | "saved";
export type CreateProductStep = "input" | "editor";

/**
 * 业务层挂在 ClipUploadFile.response 上的图片元数据。
 *
 * - `assetId` / `source`：保存到 UserProductImage 的字段
 * - `description`：即 caption，提交时同步到 metadata.imageCaptions
 * - `aiAnalyzed`：true 表示 caption 已确定（用户手填 / AI 分析过 / 链接预解析），
 *    用来驱动底部 AI Analyze / Save 按钮切换
 */
export interface ProductImageResponse {
  assetId?: number;
  description?: string;
  aiAnalyzed: boolean;
  source: UserProductImageSource;
}

/** UI 层的卖点列表项 */
export interface SellingPointItem {
  id: string;
  content: string;
  source?: UserProductSellingPointSource;
  checked: boolean;
}

/** 表单聚合状态（图片放在 imageFiles 里独立维护） */
export interface ProductFormState {
  sourceType: UserProductUpsertRequestSourceType;
  sourceProductId?: string;
  sourceUrl?: string;
  country?: string;
  title: string;
  category: string;
  categorySource?: UserProductCategorySource;
  sellingPoints: SellingPointItem[];
}

export interface CreateProductEntityModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * 创建或编辑成功后回调；新建场景下回传 createUserProduct 返回的商品 detail，
   * 调用方可据此把商品回填到外部表单（如 VideoRemakeStudio）。
   */
  onSuccess?: (detail?: any) => void;
  /** 编辑态时传入商品 id；不传则为新建 */
  productId?: number | null;
  /** 默认选哪种模式打开，缺省为 link */
  defaultMode?: CreateProductMode;
}

/** 图片槽位最大数量（按设计稿：1 主图 + 2 其他） */
export const MAX_PRODUCT_IMAGES = 6;
/** 卖点最大数量（按 PRD） */
export const MAX_SELLING_POINTS = 10;
/** 单条卖点最大字符 */
export const SELLING_POINT_MAX_LENGTH = 100;
/** 卖点 AI 生成消耗的积分 */
export const SELLING_POINTS_AI_CREDITS = 20;
/** 商品标题最大字符 */
export const PRODUCT_TITLE_MAX_LENGTH = 200;
/** 图片 caption 最大字符 */
export const IMAGE_CAPTION_MAX_LENGTH = 20;

export type ProductImageFile = ClipUploadFile;
